import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useParams, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { MessageCircle, LogIn, LogOut, User, Search, ChevronDown } from "lucide-react";

import ChatPulseHome from "@/pages/chatpulse/ChatPulseHome";
import ChatPulseCompose from "@/pages/chatpulse/ChatPulseCompose";
import ChatPulseThread from "@/components/chatpulse/ChatPulseThread";
import ArticlesLanding from "@/pages/articles/ArticlesLanding";
import ArticleWrite from "@/pages/articles/ArticleWrite";
import ArticleReader from "@/pages/articles/ArticleReader";
import PlacesLanding from "@/pages/places/PlacesLanding";
import PlaceDetail from "@/pages/places/PlaceDetail";
import NearMe from "@/pages/places/NearMe";
import CategoriesLanding from "@/pages/categories/CategoriesLanding";
import CategoryDetail from "@/pages/categories/CategoryDetail";
import SignIn from "@/pages/SignIn";
import EmbedPreview from "@/pages/EmbedPreview";
import SocialPulseCallback from "@/pages/SocialPulseCallback";
import { startSocialPulseLogin, getSocialPulseSsoConfig } from "@/lib/socialpulseOidc";

/**
 * ChatsPulse — standalone forum app (rev 2026-02-22).
 *
 * Route surface:
 *   /                             → ChatPulseHome (discovery + participation)
 *   /thread/:threadId             → ChatPulseThread reader
 *   /auth/socialpulse-login       → Kick off OIDC handshake w/ SocialPulse hub
 *   /auth/socialpulse-callback    → Consume id_token + mint session cookie
 *   /*                            → 404 fallback (redirects home)
 */

function TopNav() {
  const { user, logout } = useAuth();
  const linkCls = ({ isActive }) =>
    `text-sm font-semibold px-2 py-1 rounded-md transition-colors ${
      isActive
        ? "text-blue-700"
        : "text-slate-700 hover:text-blue-700"
    }`;
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-30" data-testid="chatspulse-header">
      <div className="max-w-6xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-6">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="chatspulse-brand">
          <span className="w-9 h-9 rounded-xl bg-blue-600 text-white inline-flex items-center justify-center shadow-sm">
            <MessageCircle className="w-5 h-5" />
          </span>
          <span className="leading-tight">
            <span className="block font-black text-slate-900 tracking-tight">ChatsPulse<span className="text-blue-600">.pro</span></span>
            <span className="block text-[10px] text-slate-400 uppercase tracking-widest">A SocialPulse Community</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-2 ml-4">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavItemDropdown label="Explore" testId="nav-explore" items={[
            { to: "/places",        label: "Places" },
            { to: "/communities",   label: "Communities" },
            { to: "/trending",      label: "Trending" },
            { to: "/questions",     label: "Questions" },
          ]} />
          <NavItemDropdown label="Categories" testId="nav-categories" items={[
            { to: "/c/local-news",  label: "Local News" },
            { to: "/c/travel",      label: "Travel" },
            { to: "/c/food",        label: "Food" },
            { to: "/c/farming",     label: "Farming" },
            { to: "/c/community",   label: "Community" },
            { to: "/c/health",      label: "Health" },
          ]} />
          <NavLink to="/articles" className={linkCls} data-testid="nav-articles">Articles</NavLink>
          <NavLink to="/about"  className={linkCls} data-testid="nav-about">About</NavLink>
          <NavLink to="/help"   className={linkCls} data-testid="nav-help">Help</NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
            aria-label="Search"
            data-testid="header-search-btn"
            onClick={() => {
              const el = document.querySelector('[data-testid="chatpulse-search-input"]');
              if (el) el.focus();
            }}
          >
            <Search className="w-4 h-4" />
          </button>
          {user ? (
            <>
              <span className="text-xs text-slate-500 hidden sm:inline-flex items-center gap-1.5" data-testid="chatspulse-user-chip">
                <User className="w-3.5 h-3.5" /> {user.name || user.email || String(user.id || "").slice(0, 8)}
              </span>
              <button
                onClick={logout}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                data-testid="chatspulse-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </>
          ) : (
            <Link
              to="/signin"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 shadow-sm"
              data-testid="chatspulse-signin-btn"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign in
            </Link>
          )}
          <button
            type="button"
            className="hidden sm:inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-semibold text-slate-600 hover:text-blue-700"
            aria-label="Language"
            data-testid="header-language-btn"
          >
            🌐 EN <ChevronDown className="w-3 h-3" />
          </button>
        </div>
      </div>
    </header>
  );
}

function NavItemDropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className="text-sm font-semibold text-slate-700 hover:text-blue-700 px-2 py-1 inline-flex items-center gap-1"
        onMouseEnter={() => setOpen(true)}
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
      >
        {label} <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-1 w-48 z-40" data-testid={`${testId}-menu`}>
          <div className="rounded-xl bg-white border border-slate-200 shadow-lg py-1.5">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-1.5 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                data-testid={`${testId}-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {it.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AppShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <TopNav />
      <main className="flex-1">{children}</main>
      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-500" data-testid="chatspulse-footer">
        ChatsPulse is part of the SocialPulse Network · Identity by{" "}
        <a href="https://socialpulse.com" className="text-blue-600 hover:underline" rel="noopener">socialpulse.com</a>
      </footer>
    </div>
  );
}

function SocialPulseLogin() {
  // The button now points at our backend /api/auth/socialpulse/init
  // which handles PKCE + state server-side and 302s to the hub.
  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const rawNext = params.get("next") || "/";
    // Only same-origin returns.
    const returnTo = rawNext.startsWith("/") ? rawNext : "/";
    window.location.replace(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/socialpulse/init?return_to=${encodeURIComponent(returnTo)}`,
    );
  }, []);
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="chatspulse-signin-redirect">
      <MessageCircle className="w-8 h-8 text-blue-600 mx-auto" />
      <p className="mt-4 text-sm text-slate-600" data-testid="signin-redirect-going">
        Redirecting you to SocialPulse to sign in…
      </p>
      <p className="mt-2 text-xs text-slate-400">
        If nothing happens, <Link to="/signin" className="text-blue-600 hover:underline">use another sign-in</Link>.
      </p>
    </div>
  );
}

function ThreadPage() {
  const { threadId } = useParams();
  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-6 py-6" data-testid="chatspulse-thread-page">
      <div className="mb-4">
        <Link to="/" className="text-xs uppercase tracking-widest text-slate-400 hover:text-blue-700 font-semibold" data-testid="chatspulse-thread-back">
          ← Back to all threads
        </Link>
      </div>
      <ChatPulseThread threadId={threadId} contextType="site" contextId="chatspulse" />
    </div>
  );
}

function NotFound() {
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell>
          <Routes>
            <Route path="/" element={<ChatPulseHome />} />
            <Route path="/places" element={<PlacesLanding />} />
            <Route path="/places/near-me" element={<NearMe />} />
            <Route path="/places/:slug" element={<PlaceDetail />} />
            <Route path="/categories" element={<CategoriesLanding />} />
            <Route path="/c/:slug" element={<CategoryDetail />} />
            <Route path="/thread/new" element={<ChatPulseCompose />} />
            <Route path="/thread/:threadId" element={<ThreadPage />} />
            <Route path="/articles" element={<ArticlesLanding />} />
            <Route path="/articles/new" element={<ArticleWrite />} />
            <Route path="/articles/:slug/edit" element={<ArticleWrite />} />
            <Route path="/articles/:slug" element={<ArticleReader />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/embed-preview" element={<EmbedPreview />} />
            <Route path="/auth/socialpulse-login" element={<SocialPulseLogin />} />
            <Route path="/auth/socialpulse-callback" element={<SocialPulseCallback />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppShell>
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}
